import React, { useState, useEffect } from 'react';
import { useData } from '../../../data';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
} from '@tanstack/react-table';
import { columns } from './data';
import Header from '../../../components/header/Header';
import { Box } from '@chakra-ui/react';
import EditEventModal from './Modal/EditEventModal';
import DeleteEventModal from './Modal/DeleteEventModal';
import RSVPStatsModal from './Modal/RSVPStatsModal';
import { useSelector } from 'react-redux';

const TableSkeleton = () => {
  return (
    <div className="animate-pulse">
      <div className="h-12 bg-gray-200 rounded mb-4" /> {/* Header */}
      {[...Array(5)].map((_, index) => (
        <div key={index} className="h-16 bg-gray-100 rounded mb-2" /> /* Rows */
      ))}
    </div>
  );
};

const EventAdmin = () => {
  const [events, setEvents] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRsvpStatsModalOpen, setIsRsvpStatsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const loggedInUser = useSelector((state) => state?.user?.userInfo);
  const loggedInUserType = loggedInUser?.userTypeTitle || '';

  const {
    data: AllEventsData,
    isLoading: isLoadingAllEvents,
    refetch: refetchEvents,
  } = useData(
    `admins/events?page_no=${page}&page_size=${pageSize}`,
    30000,
    {},
    1,
    true,
  );

  useEffect(() => {
    if (AllEventsData) {
      setEvents(AllEventsData?.data?.events || []);
      setTotalPages(AllEventsData?.data?.pagination?.total_pages);
    }
  }, [AllEventsData]);

  const handleEdit = (event) => {
    setSelectedEvent(event);
    setIsEditModalOpen(true);
  };

  const handleDelete = (event) => {
    setSelectedEvent(event);
    setIsDeleteModalOpen(true);
  };

  const handleViewStats = (event) => {
    setSelectedEvent(event);
    setIsRsvpStatsModalOpen(true);
  };

  const handleChangeVisibility = (event) => {
    // TODO: Implement visibility change logic
    // console.log('Change visibility for', event);
  };

  const table = useReactTable({
    data: events,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  return (
    <>
      <Box className="w-full">
        <Header title="Events" />
        <div className="px-4 sm:px-6 lg:px-8 w-full">
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 w-full overflow-x-auto hide-scroll sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                {isLoadingAllEvents ? (
                  <div className="w-full divide-y divide-gray-300">
                    <TableSkeleton />
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <th
                              key={header.id}
                              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {table.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              className="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.column.id === 'actions'
                                  ? {
                                      ...cell.getContext(),
                                      onEdit: handleEdit,
                                      onDelete: handleDelete,
                                      onViewStats: handleViewStats,
                                      onChangeVisibility:
                                        handleChangeVisibility,
                                      loggedInUserType,
                                    }
                                  : cell.getContext(),
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
            {isLoadingAllEvents ? (
              <div className="w-full">
                <div className="animate-pulse h-8 bg-gray-200 rounded w-48" />
              </div>
            ) : (
              <>
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing page <span className="font-medium">{page}</span>{' '}
                      of <span className="font-medium">{totalPages}</span>
                    </p>
                  </div>
                  <div>
                    <nav
                      className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                      aria-label="Pagination"
                    >
                      <button
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                        className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                      >
                        <span className="sr-only">Previous</span>
                        <svg
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages}
                        className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                      >
                        <span className="sr-only">Next</span>
                        <svg
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </Box>
      {isEditModalOpen && (
        <EditEventModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          event={selectedEvent}
          onSuccess={() => {
            setIsEditModalOpen(false);
            refetchEvents();
          }}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteEventModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          event={selectedEvent}
          onSuccess={() => {
            setIsDeleteModalOpen(false);
            refetchEvents();
          }}
        />
      )}
      {isRsvpStatsModalOpen && (
        <RSVPStatsModal
          isOpen={isRsvpStatsModalOpen}
          onClose={() => setIsRsvpStatsModalOpen(false)}
          event={selectedEvent}
        />
      )}
    </>
  );
};

export default EventAdmin;
